import type { WhyChooseUsContent } from "../../types/page-content";

interface Props {
  content: WhyChooseUsContent;
  onEstimateClick: () => void;
  sectionNumber?: string;
}

export default function WhyChooseUsSection({ content, onEstimateClick, sectionNumber = "05" }: Props) {
  return (
    <section className="relative py-28 lg:py-40 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('${content.bgImage}')` }}
      />
      <div className="absolute inset-0 bg-[#111111]/88" />

      <div className="absolute right-6 top-10 hidden xl:block">
        <span className="font-heading text-white/[0.03] text-[180px] leading-none select-none">{sectionNumber}</span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left */}
          <div className="flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-0.5 bg-safety-500" />
                <span className="font-body text-safety-500 text-xs uppercase tracking-widest2 font-700">{content.eyebrow}</span>
              </div>
              <h2 className="font-heading text-white text-6xl lg:text-8xl tracking-display leading-none mb-5">
                {content.heading}
              </h2>
              <p className="font-body text-safety-500 text-base font-700 mb-8">{content.subheading}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-px bg-asphalt-600 mt-8">
              {content.stats.map((stat) => (
                <div key={stat.label} className="bg-[#111111] px-4 py-6 text-center">
                  <div className="font-heading text-safety-500 text-5xl tracking-display leading-none mb-1">
                    {stat.num}
                  </div>
                  <div className="font-body text-white text-xs uppercase tracking-widest font-700 mb-1">{stat.label}</div>
                  <div className="font-body text-asphalt-400 text-xs">{stat.desc}</div>
                </div>
              ))}
            </div>

            <button
              onClick={onEstimateClick}
              className="mt-8 flex items-center gap-2 bg-safety-500 text-asphalt-950 font-body font-700 text-xs uppercase tracking-widest px-8 py-4 hover:bg-safety-400 transition-colors cursor-pointer whitespace-nowrap w-fit"
            >
              <i className="ri-file-list-3-line" />
              Get Your Free Estimate
            </button>
          </div>

          {/* Right — benefits grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-asphalt-600">
            {content.benefits.map((b) => (
              <div key={b.title} className="bg-[#191919] p-6 flex flex-col gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-safety-500/10 border border-safety-500/20">
                  <i className={`${b.icon} text-safety-500 text-lg`} />
                </div>
                <h3 className="font-heading text-white text-xl tracking-display">{b.title}</h3>
                <p className="font-body text-asphalt-300 text-xs leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
